import axios from "axios";
import dayjs from "dayjs";
import React, { useEffect, useState, useRef } from "react";
import Dropzone from "react-dropzone";
import state from "../scenes/idupload/stati.json";
import comuni from "../scenes/idupload/comuni.json";
import { Box } from "@mui/material";
import LoadingOverlay from "./LoadingOverlay";
import {
  ALLOWED_ID_IMAGE_ACCEPT,
  ID_IMAGE_ONLY_MESSAGE,
  isAllowedIdImageFile,
} from "./uploadFileTypes";
import { countries } from "country-data";
import {
  ContractConsent,
  Icon,
  InlineHint,
  LegalNotice,
  SectionLabel,
  UploadZoneView,
  V0_CONSENT_DISABLED_HINT,
  patchAgreementTimes,
} from "./CheckInDesign";
import { compressImageForUploadSafe } from "./imageCompression";
import { shouldAlwaysShowSuccessfulSubmit } from "./featureFlags";
import GoogleTranslateWidget from "./GoogleTranslateWidget";
import { getPreferredIdUploadLanguage } from "./languagePreference";
import { translateAgreementText } from "./agreementTranslation";
import {
  getLocalizedErrorCopy,
  getUploadErrorMessage,
} from "./userFacingErrors";

const REACT_APP_BASE_URL = process.env.REACT_APP_BASE_URL;

export const normalizeKey = (value) =>
  String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();

const getTestProgress = () => {
  /* istanbul ignore next -- browser-only component; this guard protects non-DOM imports */
  if (typeof window === "undefined") return null;

  const parseProgress = (raw) => {
    if (raw === null) return null;
    const parsed = Number(raw);
    return Number.isNaN(parsed) ? null : parsed;
  };

  const fromSearch = parseProgress(
    new URLSearchParams(window.location.search).get("testProgress")
  );
  if (fromSearch !== null) return fromSearch;

  const hash = window.location.hash || "";
  const hashQueryIndex = hash.indexOf("?");
  if (hashQueryIndex !== -1) {
    const fromHash = parseProgress(
      new URLSearchParams(hash.slice(hashQueryIndex)).get("testProgress")
    );
    if (fromHash !== null) return fromHash;
  }

  if (typeof window.__UPLOAD_TEST_PROGRESS__ === "number") {
    return window.__UPLOAD_TEST_PROGRESS__;
  }

  return null;
};

// Fast lookups for mapping OCR strings -> official table rows
const COMUNI_BY_NAME = new Map(
  comuni
    .filter((c) => c && c.Descrizione)
    .map((c) => [normalizeKey(c.Descrizione), c])
);

const STATI_BY_ISO3 = new Map(
  state
    .filter((s) => s && s.ISO)
    .map((s) => [normalizeKey(s.ISO), s])
);

const STATI_BY_NAME = new Map(
  state
    .filter((s) => s && s.Descrizione)
    .map((s) => [normalizeKey(s.Descrizione), s])
);

const EN_COUNTRY_NAME_TO_ISO3 = new Map(
  countries.all
    .filter((c) => c && c.name && c.alpha3)
    .map((c) => [normalizeKey(c.name), normalizeKey(c.alpha3)])
);

export const extractCandidates = (raw) => {
  const s = String(raw || "").trim();
  if (!s) return [];
  const out = [s];

  // Common patterns: "CITY (COUNTRY)", "CITY, COUNTRY", "CITY - COUNTRY"
  const paren = s.match(/\(([^)]+)\)/);
  if (paren && paren[1]) out.push(paren[1]);

  if (s.includes(",")) {
    const parts = s.split(",").map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) out.push(parts[parts.length - 1]);
  }

  if (s.includes("-")) {
    const parts = s.split("-").map((p) => p.trim()).filter(Boolean);
    if (parts.length > 1) out.push(parts[parts.length - 1]);
  }

  // de-dup while preserving order
  return Array.from(new Set(out));
};

export const resolveAlloggiatiPlace = (raw) => {
  const candidates = extractCandidates(raw);
  for (const candidate of candidates) {
    const key = normalizeKey(candidate);

    const comune = COMUNI_BY_NAME.get(key);
    if (comune) return { value: comune, scope: "italy" };

    // 1) Match by Italian name from Alloggiati stati table
    const byName = STATI_BY_NAME.get(key);
    if (byName) return { value: byName, scope: "abroad" };

    // 2) Match by ISO3 code directly
    const byIso3 = STATI_BY_ISO3.get(key);
    if (byIso3) return { value: byIso3, scope: "abroad" };

    // 3) Match by English country name (Mindee sometimes returns English)
    const iso3FromEnglish = EN_COUNTRY_NAME_TO_ISO3.get(key);
    if (iso3FromEnglish) {
      const byEnglishIso3 = STATI_BY_ISO3.get(iso3FromEnglish);
      /* istanbul ignore next -- country-data ISO3 values should exist in the Alloggiati stati table */
      if (byEnglishIso3) return { value: byEnglishIso3, scope: "abroad" };
    }
  }
  return null;
};

export default function UploadForm({
  setIdUploaded,
  setGroupInfo,
  groupInfo = [{
    surname: "",
    givenname: "",
    gender: "",
    dateOfBirth: dayjs("2024-01-01"),
    placeOfBirth: { Descrizione: "" },
    citizenship: { Descrizione: "" },
    documentType: { Descrizione: "" },
    documentNumber: "",
    placeOfReleaseDocument: { Descrizione: "" },
  }],
  id,
  reservationInfo,
  onAutoSubmit,
  /** Called when upload-validated succeeds with no synchronous `extractedInfo` (OCR/policy runs server-side). */
  onValidatedUploadWithoutExtract,
}) {
  const testProgress = getTestProgress();
  const [selectedFiles, setSelectedFiles] = useState(undefined);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(testProgress !== null);
  const [uploadProgress, setUploadProgress] = useState(testProgress);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [selfieFile, setSelfieFile] = useState(null);
  const [selfiePreviewUrl, setSelfiePreviewUrl] = useState(null);
  const [selfieMessage, setSelfieMessage] = useState("A clear photo of your face in good lighting");
  const [documentsAccepted, setDocumentsAccepted] = useState(false);
  const [text, setText] = useState("");
  const [checkbox, setCheckbox] = useState(false);
  const [pageError, setPageError] = useState(null);
  const [agreementLines, setAgreementLines] = useState([]);
  const [agreementLoading, setAgreementLoading] = useState(true);
  const [agreementLoadError, setAgreementLoadError] = useState(null);
  const [consentHintShow, setConsentHintShow] = useState(false);
  const consentHintTimeoutRef = useRef(null);

  const triggerConsentHint = () => {
    if (consentHintTimeoutRef.current) {
      window.clearTimeout(consentHintTimeoutRef.current);
      consentHintTimeoutRef.current = null;
    }
    setConsentHintShow(true);
    consentHintTimeoutRef.current = window.setTimeout(() => {
      setConsentHintShow(false);
      consentHintTimeoutRef.current = null;
    }, 3500);
  };

  const clearPageError = () => setPageError(null);

  const retakeSelfie = () => {
    if (selfiePreviewUrl && typeof URL.revokeObjectURL === "function") {
      URL.revokeObjectURL(selfiePreviewUrl);
    }
    setSelfiePreviewUrl(null);
    setSelfieFile(null);
    setSelfieMessage("A clear photo of your face in good lighting");
    setDocumentsAccepted(false);
    clearPageError();
  };

  useEffect(() => {
    return () => {
      if (consentHintTimeoutRef.current) {
        window.clearTimeout(consentHintTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!id) {
      setAgreementLoading(false);
      setAgreementLines([]);
      return undefined;
    }

    let cancelled = false;
    setAgreementLoading(true);
    setAgreementLoadError(null);

    const loadAgreement = async () => {
      try {
        const rentRes = await axios.get(`${REACT_APP_BASE_URL}/rentalagreement/${id}`);
        if (cancelled) return;
        const raw = typeof rentRes.data === "string" ? rentRes.data : String(rentRes.data || "");
        let localizedAgreement = raw;
        try {
          localizedAgreement = await translateAgreementText(
            raw,
            getPreferredIdUploadLanguage()
          );
        } catch {
          localizedAgreement = raw;
        }
        if (cancelled) return;
        setAgreementLines(localizedAgreement.split("\n"));
        setAgreementLoading(false);
      } catch (error) {
        if (cancelled) return;
        setAgreementLines([]);
        setAgreementLoading(false);
        setAgreementLoadError(
          getLocalizedErrorCopy("agreement_load", getPreferredIdUploadLanguage()).short
        );
      }
    };

    loadAgreement();

    return () => {
      cancelled = true;
    };
  }, [id]);

  const onDropDocument = (files) => {
    if (files.length > 0) {
      if (!isAllowedIdImageFile(files[0])) {
        setText("Upload an image of your ID.");
        setPageError(ID_IMAGE_ONLY_MESSAGE);
        setPreviewUrl(null);
        setSelectedFiles(null);
        return;
      }
      clearPageError();
      setDocumentsAccepted(false);
      setSelectedFiles(files);
      setPreviewUrl(
        isAllowedIdImageFile(files[0]) && typeof URL.createObjectURL === "function"
          ? URL.createObjectURL(files[0])
          : null
      );
    }
  };

  const onDropSelfie = (files) => {
    if (files.length === 0) return;
    const file = files[0];
    if (!file.type.startsWith("image/")) {
      setSelfieMessage("Please choose an image file (JPG, PNG, or HEIC).");
      setPageError("Selfie must be an image file.");
      return;
    }
    clearPageError();
    setDocumentsAccepted(false);
    setSelfieFile(file);
    setSelfieMessage("Selfie added. We will verify it before submitting.");
    setSelfiePreviewUrl((prev) => {
      if (prev && typeof URL.revokeObjectURL === "function") {
        URL.revokeObjectURL(prev);
      }
      return typeof URL.createObjectURL === "function" ? URL.createObjectURL(file) : null;
    });
  };

  const buildGroupInfoFromExtractedInfo = (prevGroupInfo, extractedInfo) => {
    const defaulted = extractedInfo._defaulted || {};

    const nationalityIso3 = normalizeKey(extractedInfo.nationality);
    const isNationalityItalian = nationalityIso3 === "ITA";

    const resolvedCitizenship =
      STATI_BY_ISO3.get(nationalityIso3) ||
      (isNationalityItalian ? STATI_BY_ISO3.get("ITA") : null) ||
      null;

    const birthResolved = resolveAlloggiatiPlace(extractedInfo.placeOfBirth);
    const releaseResolved = resolveAlloggiatiPlace(extractedInfo.placeOfReleaseDocument);

    const fallbackAbroad = !isNationalityItalian && resolvedCitizenship ? resolvedCitizenship : null;

    const defaultBirthPlace = COMUNI_BY_NAME.get("ROMA");
    const birthValue = birthResolved?.value || fallbackAbroad || defaultBirthPlace;
    const birthScope = birthResolved?.scope || (fallbackAbroad ? "abroad" : null);
    const birthWasProvided = String(extractedInfo.placeOfBirth || "").trim() !== "";
    const birthNeedsReview = birthWasProvided && !birthResolved?.value && !fallbackAbroad;

    const releaseValue = releaseResolved?.value || fallbackAbroad || defaultBirthPlace;
    const releaseScope = releaseResolved?.scope || (fallbackAbroad ? "abroad" : null);
    const releaseWasProvided = String(extractedInfo.placeOfReleaseDocument || "").trim() !== "";
    const releaseNeedsReview = releaseWasProvided && !releaseResolved?.value && !fallbackAbroad;

    const docType =
      extractedInfo.documentType === "PASSPORT"
        ? { Codice: "PASOR", Descrizione: "PASSAPORTO ORDINARIO" }
        : { Codice: "IDENT", Descrizione: "CARTA DI IDENTITA'" };

    return prevGroupInfo.map((member, index) =>
      index === 0
        ? {
            ...member,
            surname: extractedInfo.surname,
            givenname: extractedInfo.givenname,
            gender:
              extractedInfo.gender === ""
                ? "Male"
                : extractedInfo.gender === "M"
                ? "Male"
                : "Female",
            dateOfBirth: dayjs(extractedInfo.birthDate),
            documentNumber: extractedInfo.documentNumber,
            citizenship: resolvedCitizenship || STATI_BY_ISO3.get("ITA"),
            placeOfBirth: birthValue,
            ...(birthScope ? { placeOfBirthScope: birthScope } : {}),
            placeOfReleaseDocument: releaseValue,
            ...(releaseScope ? { placeOfReleaseDocumentScope: releaseScope } : {}),
            documentType: docType,
            __defaulted: {
              ...(member.__defaulted || {}),
              givenname: !!defaulted.givenname,
              surname: !!defaulted.surname,
              dateOfBirth: !!defaulted.birthDate,
              documentNumber: !!defaulted.documentNumber,
              gender: !!defaulted.gender,
              citizenship: !!defaulted.citizenship,
              placeOfBirth: birthNeedsReview ? true : !!defaulted.placeOfBirth,
              documentType: !!defaulted.documentType,
              placeOfReleaseDocument: releaseNeedsReview ? true : !!defaulted.placeOfReleaseDocument,
            },
          }
        : member
    );
  };

  const upload = async (e) => {
    e.preventDefault();
    const alwaysShowSuccess = shouldAlwaysShowSuccessfulSubmit();

    const file = selectedFiles[0];
    setLoading(true);
    setUploadProgress(0);
    setDocumentsAccepted(false);
    clearPageError();
    setMessage("Verifying your selfie and ID…");
    // Ensure the loading overlay can render even if axios resolves immediately (e.g. in tests)
    await Promise.resolve();
    let uploadFile = file;
    let uploadSelfieFile = selfieFile;
    try {
      [uploadFile, uploadSelfieFile] = await Promise.all([
        compressImageForUploadSafe(file),
        compressImageForUploadSafe(selfieFile),
      ]);
    } catch (compressionError) {
      // Never leave the guest on a permanent spinner: fall back to the originals.
      console.warn("[UploadForm] Image compression failed, uploading originals:", compressionError);
    }
    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("selfie", uploadSelfieFile);
    formData.append("reservationInfo", JSON.stringify(reservationInfo || {}));

    try {
      const response = await axios.post(
        `${REACT_APP_BASE_URL}/idupload/upload-validated/${id}`,
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const total = progressEvent?.total;
            if (!total) return;
            const percent = Math.round((progressEvent.loaded * 100) / total);
            setUploadProgress(percent);
          },
          timeout: 120000,
        }
      );
      if (alwaysShowSuccess) {
        setDocumentsAccepted(true);
        setMessage("You're all set!");
        setLoading(false);
        setUploadProgress(null);
        if (typeof onValidatedUploadWithoutExtract === "function") {
          onValidatedUploadWithoutExtract();
        }
        return;
      }
      setDocumentsAccepted(true);
      setMessage("You're all set!");
      const extractedInfo = response.data?.extractedInfo;
      if (response.data?.accepted === true && (
        extractedInfo == null || extractedInfo._mindeeFailed ||
        !extractedInfo.surname || !extractedInfo.givenname || !extractedInfo.documentNumber ||
        response.data.valid === false
      )) {
        setLoading(false);
        setUploadProgress(null);
        if (typeof onValidatedUploadWithoutExtract === "function") {
          onValidatedUploadWithoutExtract();
        }
        return;
      }
      if (extractedInfo == null) {
        setDocumentsAccepted(false);
        setLoading(false);
        setUploadProgress(null);
        setPageError(getLocalizedErrorCopy("temporary", getPreferredIdUploadLanguage()).short);
        return;
      }
      if (extractedInfo.surname === undefined || extractedInfo._mindeeFailed) {
        setLoading(false);
        setUploadProgress(null);
        setPageError(
          getLocalizedErrorCopy("temporary", getPreferredIdUploadLanguage()).short
        );
      } else {
        const nextGroupInfo = buildGroupInfoFromExtractedInfo(groupInfo, extractedInfo);
        setGroupInfo(nextGroupInfo);
        localStorage.setItem("groupInfo", JSON.stringify(nextGroupInfo));

        setMessage("You're all set!");
        if (onAutoSubmit) {
          await onAutoSubmit(nextGroupInfo);
        } else {
          setIdUploaded((prevIdUploaded) => !prevIdUploaded);
        }
        setLoading(false);
        setUploadProgress(null);
      }
    } catch (err) {
      console.error("Upload error:", err);
      if (alwaysShowSuccess) {
        setLoading(false);
        setUploadProgress(null);
        setDocumentsAccepted(true);
        setMessage("You're all set!");
        clearPageError();
        if (typeof onValidatedUploadWithoutExtract === "function") {
          onValidatedUploadWithoutExtract();
        }
        return;
      }
      setLoading(false);
      setUploadProgress(null);
      setDocumentsAccepted(false);
      const lang = getPreferredIdUploadLanguage();
      setPageError(getUploadErrorMessage(err, lang));
    }
  };
  const uploadState = loading ? "uploading" : selectedFiles ? "verified" : "idle";
  const selfieUploadState = loading ? "uploading" : selfieFile ? "verified" : "idle";
  const consentDisabled = loading || !selectedFiles || !selfieFile;
  const submitDisabled = !selectedFiles || !selfieFile || !checkbox || loading;
  const submitButtonLabel = "Confirm & Continue";
  const renderedAgreementLines = patchAgreementTimes(agreementLines, reservationInfo) || [];

  return (
    <Box data-testid="upload-form" className="v0-card-stack">
      <LoadingOverlay
        loading={loading}
        message={
          message.trim()
            ? message
            : "Verifying your selfie and ID…"
        }
        progress={uploadProgress}
      />
      {pageError ? (
        <div className="v0-page-error-banner" role="status" aria-live="polite">
          <span className="v0-page-error-icon" aria-hidden="true">
            <Icon name="info" size={22} />
          </span>
          <p className="v0-page-error-text">{pageError}</p>
        </div>
      ) : null}
      {documentsAccepted && !pageError ? (
        <div className="v0-page-success-banner" role="status" aria-live="polite">
          <span className="v0-page-success-icon" aria-hidden="true">
            <Icon name="check" size={22} />
          </span>
          <div className="v0-page-success-copy">
            <p className="v0-page-success-text">{"You're all set!"}</p>
            <p className="v0-page-success-detail">
              We look forward to welcoming you at our property. Want to add more guests? Just upload their ID
              below.
            </p>
          </div>
        </div>
      ) : null}
      <div>
        <Dropzone
          onDrop={onDropDocument}
          multiple={false}
          accept={ALLOWED_ID_IMAGE_ACCEPT}
          disabled={loading}
        >
          {({ getRootProps, getInputProps }) => (
            <section aria-labelledby="upload-heading">
              <SectionLabel id="upload-heading">Identity Document</SectionLabel>
              <UploadZoneView
                state={uploadState}
                previewUrl={previewUrl}
                fileName={selectedFiles && selectedFiles[0].name ? selectedFiles[0].name : null}
                message={text}
                rootProps={getRootProps({
                  className: "dropzone",
                  "data-testid": "upload-id-root",
                })}
                inputProps={getInputProps({
                  "aria-label": "Upload ID document",
                  accept: Object.values(ALLOWED_ID_IMAGE_ACCEPT).flat().join(","),
                })}
              />
            </section>
          )}
        </Dropzone>
        <Dropzone
          onDrop={onDropSelfie}
          multiple={false}
          accept={{ "image/*": [] }}
          disabled={loading}
        >
          {({ getRootProps, getInputProps }) => (
            <section className="mt-3" aria-labelledby="selfie-heading">
              <SectionLabel id="selfie-heading">Selfie</SectionLabel>
              <UploadZoneView
                mode="selfie"
                state={selfieUploadState}
                previewUrl={selfiePreviewUrl}
                fileName={selfieFile?.name || null}
                message={selfieMessage}
                rootProps={getRootProps({
                  className: "dropzone",
                  "data-testid": "upload-selfie-root",
                })}
                inputProps={getInputProps({
                  "aria-label": "Upload selfie photo",
                  accept: "image/*",
                })}
                onVerifiedReplace={loading ? undefined : retakeSelfie}
              />
            </section>
          )}
        </Dropzone>
        <section className="mt-3" aria-labelledby="consent-heading">
          <div className="v0-consent-heading-row">
            <SectionLabel id="consent-heading">Rental Agreement</SectionLabel>
            <GoogleTranslateWidget placement="agreement" />
          </div>
          <ContractConsent
            checked={checkbox}
            disabled={consentDisabled}
            onChange={setCheckbox}
            agreementLines={renderedAgreementLines}
            agreementLoading={agreementLoading}
            agreementError={agreementLoadError}
            consentHintShow={consentHintShow}
            onDisabledConsentInteraction={triggerConsentHint}
          />
        </section>
        <div className="mt-3">
          <LegalNotice />
        </div>
        <aside className="selected-file-wrapper v0-sticky-footer mt-3">
          <InlineHint show={consentHintShow} message={V0_CONSENT_DISABLED_HINT} />
          <button
            className="btn v0-primary-button"
            data-testid="confirm-upload-submit"
            disabled={submitDisabled}
            onClick={(e) => {
              upload(e);
            }}
          >
            {submitButtonLabel}
          </button>
        </aside>
      </div>
    </Box>
  );
}
